import type { BatchUpdateAreasDto, BatchUpdateAreasResult } from './types/resource-area';
import { createAppRequest } from '@/services/request';
import type { LeadScopedViewMode } from '@/modules/zy-crm/utils/lead-view';
import type { ApiRequestOptions } from './types/common';
import type {
  AssignLeadDto,
  BatchAssignLeadDto,
  BatchDeleteLeadDto,
  BatchClaimLeadDto,
  BatchRescoreLeadDto,
  BatchUpdateLeadStatusDto,
  CreateLeadDto,
  ImportLeadFromMarketingDto,
  ImportLeadFromMarketingResponse,
  CreateLeadTeamMemberDto,
  LeadImportConflictItem,
  LeadImportConflictQueryParams,
  LeadAiRecommendationResult,
  LeadDrawerProfile,
  LeadDrawerSummaryPanel,
  LeadItem,
  LeadPageResult,
  LeadQueryParams,
  LeadSmartDraftRequest,
  LeadSmartDraftResponse,
  LeadWorkbenchResult,
  LeadWorkbenchViewMode,
  LeadSourceAnalysisQuery,
  LeadSourceAnalysisResult,
  PutLeadToPoolDto,
  PutLeadToPoolResponse,
  ResolveLeadImportConflictDto,
  TransformLeadDto,
  TransformLeadResponse,
  UpdateLeadDto,
} from './types/lead';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/leads';

type DetailScopeOptions = ApiRequestOptions & {
  params?: Record<string, unknown>;
  scope?: 'self' | 'team' | 'all';
  viewMode?: LeadScopedViewMode;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const normalizeLeadPage = (resp: unknown): LeadPageResult => {
  if (!isObject(resp)) {
    throw new Error('线索列表返回结构异常，请检查 API 地址或代理配置');
  }

  const root = resp as Record<string, unknown>;
  const candidates: unknown[] = [
    root,
    root.data,
    root.result,
    root.payload,
    (root.response as Record<string, unknown> | undefined)?.data,
  ];

  for (const candidate of candidates) {
    if (!isObject(candidate)) {
      continue;
    }
    const pageObj = candidate as Record<string, unknown>;
    const hasList = Array.isArray(pageObj.list);
    const hasItems = Array.isArray(pageObj.items);
    const hasDataArray = Array.isArray(pageObj.data);
    const data: unknown[] = hasList
      ? (pageObj.list as unknown[])
      : hasItems
        ? (pageObj.items as unknown[])
        : hasDataArray
          ? (pageObj.data as unknown[])
          : [];
    const totalRaw = pageObj.total ?? pageObj.count ?? data.length;
    const pageRaw = pageObj.page ?? pageObj.current ?? 1;
    const pageSizeRaw =
      (pageObj.pageSize ?? pageObj.limit ?? data.length) || 20;

    if (
      hasList ||
      hasItems ||
      hasDataArray ||
      pageObj.total !== undefined ||
      pageObj.count !== undefined
    ) {
      return {
        list: data as LeadItem[],
        total: Number(totalRaw) || 0,
        page: Number(pageRaw) || 1,
        pageSize: Number(pageSizeRaw) || 20,
      };
    }
  }

  throw new Error('线索列表返回结构异常，请检查 API 地址或代理配置');
};

const unwrapLeadDetail = (resp: unknown): LeadItem => {
  if (resp && typeof resp === 'object') {
    const obj = resp as Record<string, unknown>;
    const candidates = [obj, obj.data, obj.result, obj.payload, (obj.response as Record<string, unknown> | undefined)?.data];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }
      const record = candidate as Record<string, unknown>;
      if (
        typeof record.uuid === 'string' &&
        typeof record.name === 'string'
      ) {
        return candidate as LeadItem;
      }
    }
  }
  throw new Error('线索详情返回结构异常');
};

export async function queryLeads(
  params: LeadQueryParams,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(BASE_PATH, {
    method: 'GET',
    params: {
      ...params,
      tagUuids: Array.isArray(params.tagUuids)
        ? params.tagUuids.join(",")
        : params.tagUuids,
    },
    ...(options || {}),
  });
  return normalizeLeadPage(resp);
}

export async function queryLeadWorkbench(
  viewMode: LeadWorkbenchViewMode,
  options?: ApiRequestOptions,
) {
  return request<LeadWorkbenchResult>(`${BASE_PATH}/workbench`, {
    method: 'GET',
    params: { viewMode },
    ...(options || {}),
  });
}

export async function queryLeadAiRecommendations(
  payload: {
    viewMode: LeadWorkbenchViewMode;
    leadUuids: string[];
    fingerprint?: string;
  },
  options?: ApiRequestOptions,
) {
  return request<LeadAiRecommendationResult>(
    `${BASE_PATH}/workbench/ai-recommendations`,
    {
      method: 'POST',
      data: payload,
      ...(options || {}),
    },
  );
}

export async function queryLeadDetail(uuid: string, options?: DetailScopeOptions) {
  const { scope, viewMode, params, ...rest } = options || {};
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: 'GET',
    params: {
      ...(params || {}),
      ...(scope ? { scope } : {}),
      ...(viewMode ? { viewMode } : {}),
    },
    ...rest,
  });
  return unwrapLeadDetail(resp);
}

export async function queryLeadDrawerProfile(
  uuid: string,
  options?: DetailScopeOptions,
) {
  const { scope, viewMode, params, ...rest } = options || {};
  return request<LeadDrawerProfile>(`${BASE_PATH}/${uuid}/drawer-profile`, {
    method: 'GET',
    params: {
      ...(params || {}),
      ...(scope ? { scope } : {}),
      ...(viewMode ? { viewMode } : {}),
    },
    ...rest,
  });
}

export async function createLead(payload: CreateLeadDto, options?: ApiRequestOptions) {
  return request<LeadItem>(BASE_PATH, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function createLeadSmartDraft(
  payload: LeadSmartDraftRequest,
  options?: ApiRequestOptions,
) {
  return request<LeadSmartDraftResponse>(`${BASE_PATH}/smart-draft`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function importLeadFromMarketing(
  payload: ImportLeadFromMarketingDto,
  options?: ApiRequestOptions,
) {
  return request<ImportLeadFromMarketingResponse>(`${BASE_PATH}/import-from-marketing`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateLead(
  uuid: string,
  payload: UpdateLeadDto,
  options?: ApiRequestOptions,
) {
  return request<LeadItem>(`${BASE_PATH}/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function createLeadTeamMember(
  uuid: string,
  payload: CreateLeadTeamMemberDto,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteLeadTeamMember(
  uuid: string,
  teamMemberUuid: string,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members/${teamMemberUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function deleteLead(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function batchDeleteLeads(
  payload: BatchDeleteLeadDto,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/batch-delete`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function batchUpdateLeadFollowUpStatus(
  payload: BatchUpdateLeadStatusDto,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/batch-status`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function restoreLead(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/${uuid}/restore`, {
    method: 'PATCH',
    ...(options || {}),
  });
}

export async function rescoreLead(uuid: string, options?: ApiRequestOptions) {
  return request<LeadItem>(`${BASE_PATH}/${uuid}/rescore`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function batchRescoreLeads(
  payload: BatchRescoreLeadDto,
  options?: ApiRequestOptions,
) {
  return request<{ uuids: string[]; rescored: number }>(`${BASE_PATH}/batch-rescore`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function transformLead(
  uuid: string,
  payload: TransformLeadDto,
  options?: ApiRequestOptions,
) {
  return request<TransformLeadResponse>(`${BASE_PATH}/${uuid}/transform`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function putLeadToPool(
  uuid: string,
  payload: PutLeadToPoolDto,
  options?: ApiRequestOptions,
) {
  return request<PutLeadToPoolResponse>(`${BASE_PATH}/${uuid}/pool`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function claimLead(uuid: string, options?: ApiRequestOptions) {
  return request<LeadItem>(`${BASE_PATH}/${uuid}/claim`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function assignLead(
  uuid: string,
  payload: AssignLeadDto,
  options?: ApiRequestOptions,
) {
  return request<LeadItem>(`${BASE_PATH}/${uuid}/assign`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function batchClaimLeads(
  payload: BatchClaimLeadDto,
  options?: ApiRequestOptions,
) {
  return request<{ uuids: string[]; memberUuid: string }>(`${BASE_PATH}/batch-claim`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function batchAssignLeads(
  payload: BatchAssignLeadDto,
  options?: ApiRequestOptions,
) {
  return request<{ uuids: string[]; memberUuid: string }>(`${BASE_PATH}/batch-assign`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function queryLeadSourceAnalysis(
  params: LeadSourceAnalysisQuery,
  options?: ApiRequestOptions,
) {
  return request<LeadSourceAnalysisResult>(`${BASE_PATH}/source-analysis/summary`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function queryLeadImportConflicts(
  params: LeadImportConflictQueryParams,
  options?: ApiRequestOptions,
) {
  return request<{
    list: LeadImportConflictItem[];
    total: number;
    page: number;
    pageSize: number;
  }>(`${BASE_PATH}/import-conflicts`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function resolveLeadImportConflict(
  uuid: string,
  payload: ResolveLeadImportConflictDto,
  options?: ApiRequestOptions,
) {
  return request<{
    success: boolean;
    uuid: string;
    status: string;
    resolvedAction?: string;
  }>(`${BASE_PATH}/import-conflicts/${uuid}/resolve`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function batchUpdateLeadAreas(payload: BatchUpdateAreasDto, options?: ApiRequestOptions) {
  return request<BatchUpdateAreasResult>(`${BASE_PATH}/batch-areas`, {
    method: 'PATCH', data: payload, ...(options || {}),
  });
}
